/**
 * Subject forms — shared bits for creating a subject (PPT):
 *   - CategorySelect: the category dropdown with a "+ custom…" fill-in.
 *   - AddSubjectForm: prep a subject (name + category + the tool[s] that will
 *     scan it). Tools optional; tick more than one for a MULTI-TOOL subject.
 *   - nameTaken: instant client-side duplicate check (server guard authoritative).
 *
 *   - SubjectPresets: a subject's own deliverable presets (SafeShare slice 6.1),
 *     or "Job default"; changed through setSubjectPresets (whoever may write).
 *     AddSubjectForm can pick them at creation too.
 * Shared by HandheldHome (main-page "+ Add subject") and BringRawIn (Ingest raw
 * "+ add a subject" / Separate). Ported faithfully from the locked mockup
 * (components/handheldIngest/subjectForm.jsx) — wired to the real backend.
 */
import React, { useMemo, useState } from "react";
import PresetPicker from "../../../../components/PresetPicker.jsx";
import { handheld, newClientTempId } from "../../../../lib/handheldCallables.js";
import { PPT_CATEGORIES, NON_TETHERED, pad3 } from "../../../../lib/handheldNaming.js";

// Instant client-side duplicate check; the server name-guard stays authoritative.
export function nameTaken(subjects, value, selfId) {
  const norm = (value ?? "").trim().toLowerCase();
  if (!norm) return false;
  return subjects.some(
    (s) => s.id !== selfId && (s.displayName ?? "").trim().toLowerCase() === norm,
  );
}

const CUSTOM = "__custom__";
export function CategorySelect({ value, onChange, disabled = false }) {
  const isCustom = value && !PPT_CATEGORIES.includes(value);
  return (
    <select
      className="hh-cat"
      value={value}
      disabled={disabled}
      onChange={(e) => {
        if (e.target.value === CUSTOM) {
          const v = (window.prompt("Category name") ?? "").trim();
          if (v) onChange(v);
          return;
        }
        onChange(e.target.value);
      }}
    >
      {PPT_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
      {isCustom && <option value={value}>{value}</option>}
      <option value={CUSTOM}>+ custom…</option>
    </select>
  );
}

// name + category + tools → addSubject callable. onDone receives the new id.
export function AddSubjectForm({ jobId, subjects, initialCategory = "Object", onDone, onCancel, presets = [] }) {
  const [presetIds, setPresetIds] = useState([]);
  const [pickOpen, setPickOpen] = useState(false);
  const [name, setName] = useState("");
  const [category, setCategory] = useState(initialCategory);
  const [tools, setTools] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  // Stable per mount → retries of THIS add reuse it (idempotent).
  const [clientTempId] = useState(newClientTempId);

  const nextHint = useMemo(
    () => subjects.reduce((m, s) => Math.max(m, s.number ?? 0), 0) + 1,
    [subjects],
  );
  const toggleTool = (code) => setTools((t) => (t.includes(code) ? t.filter((x) => x !== code) : [...t, code]));
  const onChangeName = (v) => {
    setName(v);
    setErr(nameTaken(subjects, v) ? "Name already used on this job" : null);
  };

  const save = async () => {
    const displayName = name.trim();
    if (!displayName || busy || err) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await handheld.addSubject({ jobId, displayName, category, capturedBy: tools, clientTempId, ...(presetIds.length ? { presetIds } : {}) });
      onDone?.(res?.id);
    } catch (e) {
      setErr(e?.message ?? "Could not add subject");
      setBusy(false); // keep form open; retry reuses the same clientTempId
    }
  };
  const cancel = () => (onCancel ? onCancel() : onDone?.());

  return (
    <div className="hh-newform">
      <span className="mono hh-next-num">{pad3(nextHint)}</span>
      <input
        className={`hh-name-input ${err ? "bad" : ""}`}
        placeholder="Subject name (becomes the folder)"
        value={name}
        autoFocus
        disabled={busy}
        onChange={(e) => onChangeName(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") cancel(); }}
      />
      <CategorySelect value={category} onChange={setCategory} />
      {!!presets.length && <div>
        <button type="button" className="hh-btn ghost" disabled={busy} onClick={() => setPickOpen(!pickOpen)}>
          Deliverable: {presetIds.length ? `${presetIds.length} selected` : "job default"} ▾
        </button>
        {pickOpen && <PresetPicker presets={presets} value={presetIds} onChange={setPresetIds} disabled={busy} emptyLabel="Job default" />}
      </div>}
      <span className="hh-newform-tools">
        <span className="rw-toolslabel">Will scan with</span>
        {NON_TETHERED.map((g) => (
          <button
            type="button"
            key={g.code}
            title={g.label}
            className={`rw-tool mono ${tools.includes(g.code) ? "on" : ""}`}
            onClick={() => toggleTool(g.code)}
          >{g.code}</button>
        ))}
      </span>
      <button type="button" className="hh-btn primary" disabled={!name.trim() || !!err || busy} onClick={save}>
        {busy ? "Adding…" : "Add"}
      </button>
      <button type="button" className="hh-btn ghost" disabled={busy} onClick={cancel}>Cancel</button>
      {err && <span className="hh-name-err">{err}</span>}
    </div>
  );
}

// A subject's own deliverable presets, or "Job default" (the job's, set by an admin).
export function SubjectPresets({ jobId, subject, presets = [], canWrite }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const own = subject.presetIds ?? [];
  const label = own.length ? own.map((id) => presets.find((p) => p.presetId === id)?.name || id).join(", ") : "Job default";
  const save = async () => {
    setBusy(true); setError(null);
    try {
      await handheld.setSubjectPresets({ jobId, subjectId: subject.id, presetIds: value });
      setEditing(false);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };
  if (!canWrite) return <span className="hh-subject-presets" title="Deliverable">{label}</span>;
  return <div className="hh-subject-presets">
    <button type="button" className="hh-btn ghost" title="Deliverable" disabled={busy}
      onClick={() => { setValue(own); setError(null); setEditing(!editing); }}>{label} ▾</button>
    {editing && <div>
      <PresetPicker presets={presets} value={value} onChange={setValue} disabled={busy} emptyLabel="Job default" />
      <button type="button" className="hh-btn primary" disabled={busy} onClick={save}>{busy ? "Saving…" : "Save"}</button>
      <button type="button" className="hh-btn ghost" disabled={busy} onClick={() => setEditing(false)}>Cancel</button>
      {error && <div role="alert" className="hh-name-err">{error}</div>}
    </div>}
  </div>;
}
